import { toast } from "@fr8/frui";
import moment from "moment";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import { util } from "~/utils/util";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { unregisterNovuDeviceToken } from "~/utils/common/hooks/useNovu";


const DEVICE_ID_KEY = "frappe_device_id";
let deviceIdPromise: Promise<string> | null = null;
let _loggingOut = false;

export async function initDeviceId(): Promise<void> {
  try {
    const platform = Capacitor.getPlatform();
    if (platform !== "android" && platform !== "ios") return;
    const deviceInfo = await Device.getId();
    if (deviceInfo?.identifier) {
      localStorage.setItem(DEVICE_ID_KEY, deviceInfo.identifier);
      util.setCookie("frappe_device_id", deviceInfo.identifier, 365);
    }
  } catch {}
}

export async function getOrCreateDeviceId(): Promise<string> {
  if (typeof window === "undefined") return "";
  if (deviceIdPromise) return deviceIdPromise;
  deviceIdPromise = (async () => {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (util.getCookie(document.cookie, "frappe_device_id") as string) || null;
    }
    if (!id) {
      try {
        const platform = Capacitor.getPlatform();
        if (platform === "android" || platform === "ios") {
          const deviceInfo = await Device.getId();
          if (deviceInfo?.identifier) id = deviceInfo.identifier;
        }
      } catch {}
    }
    if (!id) {
      id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    localStorage.setItem(DEVICE_ID_KEY, id);
    util.setCookie("frappe_device_id", id, 365);
    return id;
  })();
  return deviceIdPromise;
}

const getUserStore = (): WebStorageStateStore | undefined => {
  if (typeof window !== "undefined") {
    return new WebStorageStateStore({ store: window.localStorage });
  }
  return undefined;
};

const GROW_URL =
  import.meta.env.VITE_NODE_ENV === "local"
    ? "http://localhost:5173"
    : `${import.meta.env.VITE_GROW_URL}`;
console.log({GROW_URL})
export const frappeUserManagerConfig = {
  authority: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}`,
  client_id: `${import.meta.env.VITE_PUBLIC_TRACK_CLIENT_ID}`,
  redirect_uri: `${GROW_URL}/callback`,
  response_type: "code",
  scope: "openid all offline_access",
  userinfo_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
    }/api/method/frappe.integrations.oauth2.openid_profile`,
  client_secret: `${import.meta.env.VITE_PUBLIC_TRACK_CLIENT_SECRET}`,
  grant_type: "authorization_code",
  automaticSilentRenew: false,
  post_logout_redirect_uri: `${GROW_URL}`,
  silent_redirect_uri: `${GROW_URL}/silent_renew`,
  userStore: getUserStore(),
  silentRequestTimeoutInSeconds: 50,
  metadata: {
    end_session_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/?cmd=logout`,
    authorization_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/api/method/frappe.integrations.oauth2.authorize`,
    token_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/api/method/frappe.integrations.oauth2.get_token`,
    userinfo_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/api/method/frappe.integrations.oauth2.openid_profile`,
    revocation_endpoint: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/api/method/frappe.integrations.oauth2.revoke_token`,
    jwks_uri: `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL
      }/assets/frappe_auth/client/jwks.json`,
  },
  monitorSession: false,
  revokeTokensOnSignout: true,
};

export const frappeQrUserManagerConfig = {
  ...frappeUserManagerConfig,
  // PHASE 1: /frappe-qr-callback consolidated into /callback (see routes.ts
  // and frappeCallback.tsx). Legacy redirect_uri kept in a comment for
  // reference in case rollback is needed.
  // redirect_uri: `${import.meta.env.VITE_GROW_URL}/frappe-qr-callback`,
  redirect_uri: `${import.meta.env.VITE_GROW_URL}/callback`,
  automaticSilentRenew: false,
};

let userManagerInstance: UserManager | null = null;
let qrUserManagerInstance: UserManager | null = null;

export const getFrappeUserManager = (): UserManager => {
  if (!userManagerInstance) {
    userManagerInstance = new UserManager(frappeUserManagerConfig);
  }
  return userManagerInstance;
};

export const getFrappeQrUserManager = (): UserManager => {
  if (!qrUserManagerInstance) {
    qrUserManagerInstance = new UserManager(frappeQrUserManagerConfig);
  }
  return qrUserManagerInstance;
};

export const setFrappeOidcUser = (user: User, authType?: string) => {
  const accessToken = user?.id_token || "";
  localStorage.setItem("oidcUser", JSON.stringify(user));
  localStorage.setItem("token", accessToken);
  if (authType) localStorage.setItem("frappeAuthType", authType);
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decodedToken: any = jwtDecode(token);
    const expiryTime = decodedToken?.exp;
    if (!expiryTime) return true;
    const tokenExpiry = moment(expiryTime * 1000).subtract(5, "minutes");
    return moment().isAfter(tokenExpiry);
  } catch {
    return true;
  }
};

// Safari (esp. iOS) can block or indefinitely hang the hidden iframe signinSilent()
// uses for cross-site renewal (Intelligent Tracking Prevention). The library's own
// silentRequestTimeoutInSeconds (50s) is too long to leave a user stuck on a dead
// page, so race it against a much shorter timeout and treat a hang as a failure.
const SILENT_RENEW_TIMEOUT_MS = 15 * 1000;

const signInSilent = async (userManager: UserManager) => {
  try {
    const authType = localStorage.getItem("frappeAuthType");
    if (!authType) return;
    const isNewFlow = authType === "qr" || authType === "email";
    const extraParams = isNewFlow ? await getDeviceParams(authType) : undefined;
    const user = await Promise.race([
      userManager.signinSilent({ extraTokenParams: extraParams }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("silent_renew_timeout")), SILENT_RENEW_TIMEOUT_MS)
      ),
    ]);
    if (user) {
      setFrappeOidcUser(user);
      if (isNewFlow) {
        startSessionMonitor();
      }
    }
  } catch (err) {
    console.error("signInSilent", err);
    throw err;
  }
};

const restoreUserFromLocalStorage = async (
  userManager: UserManager
): Promise<void> => {
  const oidc = localStorage.getItem("oidcUser");
  if (!oidc) return;
  const _user = new User(JSON?.parse(oidc));
  await userManager.storeUser(_user);
};

export const getFrappeUser = async (
  userManager: UserManager
): Promise<User | null> => {
  return await userManager.getUser();
};

const SESSION_CHECK_INTERVAL_MS = 60 * 1000; // 1 minute

type MonitorController = {
  start: () => void;
  stop: () => void;
};

let _monitorController: MonitorController = {
  start: () => {},
  stop: () => {},
};

export function setMonitorController(ctrl: MonitorController) {
  _monitorController = ctrl;
}

export function startSessionMonitor() {
  _monitorController.start();
}

export function stopSessionMonitor() {
  _monitorController.stop();
}

const isBrowser = () => typeof window !== "undefined";

const safeSetItem = (key: string, value: string): void => {
  if (isBrowser()) {
    localStorage.setItem(key, value);
  }
};

const safeClear = (): void => {
  if (isBrowser()) {
    localStorage.clear();
  }
};

let refreshPromise: Promise<boolean> | null = null;

const doRefresh = async (): Promise<void> => {
  if (!isBrowser()) return;
  if (!navigator.onLine) return;

  safeSetItem("lastCallTime", moment().toISOString());
  const authType = localStorage.getItem("frappeAuthType");
  if (!authType) return;
  const isNewFlow = authType === "qr" || authType === "email";
  const userManager = isNewFlow ? getFrappeQrUserManager() : getFrappeUserManager();
  const user = await getFrappeUser(userManager);
  if (!user) {
    await restoreUserFromLocalStorage(userManager);
  }
  await signInSilent(userManager);
};

const refreshToken = async (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh()
    .then(() => {
      refreshPromise = null;
      return true;
    })
    .catch((error) => {
      refreshPromise = null;
      console.error("refreshToken", error);
      handleTokenRefreshError(error);
      return false;
    });
  return refreshPromise;
};

const handleTokenRefreshError = (error: any): void => {
  const errorMessage = error.toString();

  // The silent-renew iframe hung past our own short timeout (e.g. Safari ITP blocking
  // cross-site iframe access on iOS) — this is unrecoverable, not a transient blip.
  const isSilentRenewTimeout = /silent_renew_timeout/.test(errorMessage);
  if (isSilentRenewTimeout) {
    console.warn("Silent renew timed out (likely blocked iframe), forcing logout", errorMessage);
    toast.error("Session Expired! Login Again.")
    logoutUser();
    return;
  }

  const isRateOrTempError = /error|network|timeout|abort/i.test(errorMessage)
    && !/invalid_grant|JWTExpired|revoked/.test(errorMessage);
  if (isRateOrTempError) {
    console.warn("Token refresh temporary failure, will retry on next check", errorMessage);
    return;
  }
  const isConcurrentRefresh = /concurrent_refresh/i.test(errorMessage);
  if (isConcurrentRefresh) {
    console.warn("Concurrent token refresh detected — using latest token from storage");
    return;
  }
  const isTokenExpiredError = /invalid|JWTExpired|revoked|expired/.test(
    errorMessage
  );
  const isMaxSessionsReached = /max_sessions_reached/.test(errorMessage);
  const isApprovalRequest = /request has been sent/i.test(errorMessage);
  if (isTokenExpiredError || isMaxSessionsReached) {
    const isNewFlow = ["qr", "email"].includes(localStorage.getItem("frappeAuthType") ?? "");
    toast.error(isMaxSessionsReached
      ? isApprovalRequest
        ? "Maximum device limit reached. A request has been sent for approval."
        : "Device limit reached. Please log out from another device."
      : "Session Expired! Login Again.")
    logoutUser();
  }
};

const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;
const REFRESH_FAIL_KEY = "lastTokenRefreshFail";

export const getToken = async (requestUrl?: string) => {
  if (!isBrowser() || requestUrl?.includes("delete-user-device")) return;

  const token = localStorage.getItem("token");
  const isWeb = util.getCookie(document.cookie, "devicePlatform") === "web";

  if (!token && !isWeb) {
    logoutUser();
    return;
  }

  if (!token || isTokenExpired(token)) {
    if (!localStorage.getItem("frappeAuthType")) return token;
    if (!navigator.onLine) return token;

    const lastFail = parseInt(localStorage.getItem(REFRESH_FAIL_KEY) || "0");
    if (Date.now() - lastFail < REFRESH_COOLDOWN_MS) return token;

    const ok = await refreshToken();
    if (!ok) {
      localStorage.setItem(REFRESH_FAIL_KEY, String(Date.now()));
      logoutUser();
      return;
    }
    localStorage.removeItem(REFRESH_FAIL_KEY);
    const refreshedToken = localStorage.getItem("token");
    if (refreshedToken && refreshedToken !== token) return refreshedToken;
    return token;
  }

  return token;
};

function getCsrfToken(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)X-Frappe-CSRF-Token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

function getOidcAccessToken(): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = localStorage.getItem("oidcUser");
    if (!raw) return "";
    const user = JSON.parse(raw);
    return user?.access_token || user?.id_token || "";
  } catch {
    return "";
  }
}

async function frappePost(url: string, body: Record<string, string>, useCsrf = false, retried = false, credentials: RequestCredentials = "include") {
  const headers: Record<string, string> = { "Content-Type": "application/x-www-form-urlencoded" };
  if (useCsrf) {
    const csrf = getCsrfToken();
    if (csrf) headers["X-Frappe-CSRF-Token"] = csrf;
  }
  const token = getOidcAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: new URLSearchParams(body),
    credentials,
  });

  if (res.status === 401 && !retried) {
    const authType = localStorage.getItem("frappeAuthType");
    if (authType === "qr" || authType === "email") {
      await refreshToken();
      return frappePost(url, body, useCsrf, true, credentials);
    }
  }

  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { status: "error", message: text };
  }
}

export async function deactivateCurrentDevice() {
  try {
    const deviceId = await getOrCreateDeviceId();
    const body: Record<string, string> = {
      device_id: deviceId,
      app_name: getAppPolicyDeviceType(),
    };

    const result = await frappePost(
      `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_session.deactivate_device_session`,
      body,
      true, false, "include"
    );
    if (result?.exc_type === "CSRFTokenError") {
      const fb = await frappePost(
        `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_session.deactivate_device_session`,
        body,
        false, false, "omit"
      );
      return fb.message || fb;
    }
    return result.message || result;
  } catch (error) {
    return { status: "error" };
  }
}

const withTimeout = async (promise: Promise<unknown>, ms: number): Promise<void> => {
  await Promise.race([
    promise,
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
};

export const logoutUser = async () => {
  if (_loggingOut) return;
  _loggingOut = true;
  try {
    stopSessionMonitor();
    const isNewFlow = ["qr", "email"].includes(localStorage.getItem("frappeAuthType") ?? "");
    try {
      const bc = new BroadcastChannel("frappe-auth");
      bc.postMessage({ type: "logout" });
      bc.close();
    } catch {}
    try {
      await withTimeout((async () => {
        await deactivateCurrentDevice();
        
          await unregisterNovuDeviceToken();
        
      })(), 5000);
    } catch {}
    util.clearCookie("filter");
    const savedDeviceId = localStorage.getItem(DEVICE_ID_KEY);
    safeClear();
    if (savedDeviceId) {
      localStorage.setItem(DEVICE_ID_KEY, savedDeviceId);
      util.setCookie("frappe_device_id", savedDeviceId, 365);
    }
  window.location.replace(window.location.origin + (isNewFlow ? "/employee/login" : "/employee/logout"));
  } finally {
    _loggingOut = false;
  }
};


// export async function sendSimVerificationOtp(mobile: string) {
//   try {
//     const res = await fetch(
//       `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.msg91.send_sim_verification_otp`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/x-www-form-urlencoded" },
//         body: new URLSearchParams({ mobile }),
//       }
//     );
//     return await res.json();
//   } catch (error) {
//     console.error("sendSimVerificationOtp error:", error);
//     return { status: "failed" };
//   }
// }

// export async function verifySimOtp(mobile: string, otp: string) {
//   try {
//     const res = await fetch(
//       `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.msg91.verify_sim_otp`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/x-www-form-urlencoded" },
//         body: new URLSearchParams({ mobile, otp }),
//       }
//     );
//     return await res.json();
//   } catch (error) {
//     console.error("verifySimOtp error:", error);
//     return { status: "failed" };
//   }
// }

export async function checkDeviceSession(deviceId: string, appName: string, retried = false) {
  try {
    const oidcUser = JSON.parse(localStorage.getItem("oidcUser") || "{}");
    const accessToken = oidcUser?.access_token || await getToken();
    if (!accessToken) return { is_active: false, reason: "no_token" };

    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_session.validate_device_session`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ device_id: deviceId, app_name: appName }),
      }
    );

    if (response.status === 401 && !retried) {
      await refreshToken();
      return checkDeviceSession(deviceId, appName, true);
    }

    if (response.status === 401 || response.status === 403) {
      return { is_active: false, reason: "auth_failed" };
    }

    if (response.status === 204) {
      return { is_active: true };
    }

    if (!response.ok) {
      return { is_active: true };
    }

    let data: any;
    try {
      data = await response.json();
    } catch {
      return { is_active: true };
    }

    if (data?.exc_type || data?.exception) {
      return { is_active: false, reason: data.exc_type || "frappe_error" };
    }
    return data?.message || data;
  } catch (error) {
    return { is_active: true };
  }
}

export async function scanFrappeQrCode(qrCodeData: string) {
  try {
    let payload: any;
    try {
      payload = JSON.parse(qrCodeData.trim());
    } catch {
      return { status: "error", message: "Invalid QR code format" };
    }

    if (payload.type !== "qr_login") {
      return { status: "error", message: "Not a valid login QR code" };
    }

    const json = await frappePost(
      `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.qr_login.scan_qr_session`,
      {
        qr_code: payload.qr_code,
        session_name: payload.session_name,
        device_id: await getOrCreateDeviceId(),
        device_type: getDeviceType(),
      },
      true
    );
    const result = json.message || json;
    if (result.exc_type) {
      return { status: "error", message: result.message || result._server_messages || "Scan failed" };
    }
    return result;
  } catch (error) {
    return { status: "error", message: "Network error" };
  }
}

export async function fetchUserDevices(appName?: string) {
  try {
    const params = new URLSearchParams();
    if (appName) params.set("app_name", appName);
    const res = await frappePost(
      `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_session.get_user_devices?${params}`,
      { }, true
    );
    return res.message || res;
  } catch (error) {
    return { status: "error", sessions: [] };
  }
}

export async function logoutDevice(sessionName: string) {
  try {
    const json = await frappePost(
      `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_session.logout_device`,
      { session_name: sessionName },
      true
    );
    return json.message || json;
  } catch (error) {
    return { status: "error" };
  }
}

export async function getDeviceParams(
  loginType: string,
  simInfo?: { is_detect: true; mobile_no: string | null }
) {
  const base = {
    device_id:   await getOrCreateDeviceId(),
    app_name:    getAppPolicyDeviceType(),
    device_type: getDeviceType(),
    login_type:  loginType,
    is_detect:   "true",
  };
  if (simInfo && simInfo.mobile_no) {
    return { ...base, mobile_no: simInfo.mobile_no };
  }
  return base;
}

export async function fetchMyDeviceLimitRequests(appName?: string): Promise<{
  status: string;
  requests: Array<{ name: string; status: string; app_name: string; approved_at?: string; consumed_at?: string | null }>;
}> {
  // Read-only — GET avoids CSRF. Always pass device_id so Capacitor mobile
  // (no cookie → Guest) can fall back to a device-scoped lookup on the
  // backend. Web still uses the user-scoped path because the cookie makes
  // frappe.session.user non-Guest.
  try {
    const deviceId = await getOrCreateDeviceId();
    const base = `${import.meta.env.VITE_PUBLIC_FRAPPE_AUTH_URL}/api/method/frappe_auth.api.device_limit_request.get_my_device_limit_requests`;
    const qs = new URLSearchParams();
    if (appName) qs.set("app_name", appName);
    if (deviceId) qs.set("device_id", deviceId);
    const url = qs.toString() ? `${base}?${qs.toString()}` : base;

    const headers: Record<string, string> = {};
    const token = getOidcAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let res = await fetch(url, { method: "GET", headers, credentials: "include" });
    if (res.status === 401 && token) {
      res = await fetch(url, { method: "GET", headers, credentials: "omit" });
    }
    const json = await res.json();
    return json?.message || json || { status: "error", requests: [] };
  } catch {
    return { status: "error", requests: [] };
  }
}

export function getDeviceType(): "mobile" | "tablet" | "web" | "desktop" {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === "android" || platform === "ios") {
      const ua = navigator.userAgent.toLowerCase();
      if (/ipad|tablet|android(?!.*mobile)|mac.*intel.*ipad/.test(ua)) {
        return "tablet";
      }
      if (Math.min(window.screen.width, window.screen.height) >= 600) {
        return "tablet";
      }
      return "mobile";
    }
  } catch {}
  const uaResult = util.getDeviceTypeFromUserAgent(navigator.userAgent);
  if (uaResult === "tablet") return "tablet";
  if (typeof window !== "undefined" && window.screen) {
    const minDim = Math.min(window.screen.width, window.screen.height);
    if (minDim >= 600 && window.matchMedia?.("(hover: none) and (pointer: coarse)")?.matches) {
      return "tablet";
    }
  }
  return uaResult;
}

export function getAppPolicyDeviceType(): string {
  const dt = getDeviceType();
  if (dt === "mobile") return "WOW - app";
  if (dt === "tablet") return "WOW - tab";
  return "WOW - web";
}

export function useFrappeTokenManager() {
  useEffect(() => {
    let active = false;
    let checkInterval: ReturnType<typeof setInterval> | null = null;
    let bc: BroadcastChannel | null = null;

    const clearAllIntervals = () => {
      if (checkInterval) { clearInterval(checkInterval); checkInterval = null; }
    };

    const doLogout = async (reason: string) => {
      if (!active) return;
      active = false;
      clearAllIntervals();
      toast.error(reason)
      await logoutUser();
    };

    const checkSession = async () => {
      if (!active) return;
      const authType = localStorage.getItem("frappeAuthType");
      if (authType !== "qr" && authType !== "email") return;
      try {
        const deviceId = await getOrCreateDeviceId();
        const result = await checkDeviceSession(deviceId, getAppPolicyDeviceType());
        if (result.is_active === false) {
          doLogout("Session expired on another device. Logging out.");
        }
      } catch {}
    };

    const handleOnline = () => {
      checkSession();
    };

    const start = () => {
      if (active) return;
      active = true;

      checkSession();
      checkInterval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);

      window.addEventListener("online", handleOnline);

      try {
        bc = new BroadcastChannel("frappe-auth");
        bc.onmessage = (event) => {
          if (event.data?.type === "logout") {
            if (!active) return;
            active = false;
            clearAllIntervals();
            logoutUser();
          }
        };
      } catch {}
    };

    const stop = () => {
      if (!active) return;
      active = false;
      clearAllIntervals();
      window.removeEventListener("online", handleOnline);
      if (bc) { bc.close(); bc = null; }
    };

    setMonitorController({ start, stop });

    const authType = localStorage.getItem("frappeAuthType");
    if (authType === "qr" || authType === "email") {
      start();
    }

    return () => {
      stop();
      setMonitorController({ start: () => {}, stop: () => {} });
    };
  }, []);
}

